import { resolveApiBaseUrl } from "./api/client";

/**
 * Build a URL that the browser can use to fetch a Places photo. The
 * underlying Google Maps API key lives server-side; the /v1/places/photo
 * proxy hides it and sets a 24h Cache-Control header.
 */
export function placesPhotoURL(
  photoName: string | undefined,
  opts?: { maxWidth?: number; maxHeight?: number }
): string | undefined {
  if (!photoName) return undefined;
  const baseUrl = resolveApiBaseUrl();
  const params = new URLSearchParams({ name: photoName });
  if (opts?.maxWidth) params.set("maxWidth", String(opts.maxWidth));
  if (opts?.maxHeight) params.set("maxHeight", String(opts.maxHeight));
  return `${baseUrl}/v1/places/photo?${params.toString()}`;
}

/**
 * Build a URL for the Street View static image proxy at given coordinates.
 * Used as a fallback for itinerary items that don't have a Places photo.
 * Returns 404 from the server when Google has no imagery there — the <img>
 * tag naturally hides via onError handlers in callers.
 */
export function streetViewURL(
  lat: number | undefined,
  lng: number | undefined,
  opts?: { width?: number; height?: number }
): string | undefined {
  if (!lat || !lng) return undefined;
  const baseUrl = resolveApiBaseUrl();
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
  });
  if (opts?.width) params.set("w", String(opts.width));
  if (opts?.height) params.set("h", String(opts.height));
  return `${baseUrl}/v1/places/streetview?${params.toString()}`;
}

/**
 * Returns the best available image URL for an itinerary item, preferring
 * Places photos and falling back to Street View. Returns undefined if
 * neither is available.
 */
export function itemImageURL(
  item: {
    photoNames?: string[];
    photoUrls?: string[];
    lat?: number;
    lng?: number;
  },
  opts?: { size?: number; width?: number; height?: number }
): string | undefined {
  const width = opts?.width ?? opts?.size ?? 160;
  const height = opts?.height ?? opts?.size ?? 160;
  if (item.photoNames && item.photoNames.length > 0) {
    return placesPhotoURL(item.photoNames[0], {
      maxWidth: width,
      maxHeight: height,
    });
  }
  if (item.photoUrls && item.photoUrls.length > 0) {
    return item.photoUrls[0];
  }
  return streetViewURL(item.lat, item.lng, { width, height });
}
